import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { userRequest } from "../../../utils/requestMethods";
import { FiX, FiCheck, FiAlertCircle, FiMessageSquare, FiStar, FiEdit3 } from "react-icons/fi";

const AddCommentModal = ({ closeModal, school, schoolId, onCommentAdded, childId, results, term, session }) => {
  const [comment, setComment] = useState("");
  const [remark, setRemark] = useState("");
  const [customPosition, setCustomPosition] = useState("");
  const [selectedCommentTemplate, setSelectedCommentTemplate] = useState("");
  const [selectedRemarkTemplate, setSelectedRemarkTemplate] = useState("");
  const [skillsRatings, setSkillsRatings] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  console.log(school?.settings);

  // Predefined options for comments and remarks
  const defaultComments = [
    "Excellent performance throughout the term. Shows great improvement in all subjects.",
    "Good performance with consistent effort. Student demonstrates understanding of concepts.",
    "Satisfactory performance. Student shows potential for improvement with more practice.",
    "Student has shown improvement but needs to work harder to achieve better results.",
    "Needs more attention and support. Student should focus on completing assignments on time.",
    "Outstanding academic performance. Student demonstrates exceptional understanding and application of concepts.",
    "Very good performance with room for improvement. Student should continue working hard.",
    "Average performance. Student needs to be more consistent with studies and assignments."
  ];

  const defaultRemarks = [
    "Continue to maintain this excellent standard. Keep up the good work!",
    "Keep working hard and maintain consistency in your studies.",
    "Focus on areas that need improvement and practice regularly.",
    "Put in more effort and complete all assignments on time.",
    "Seek help when needed and participate more actively in class.",
    "Excellent work! Continue to excel and set a good example for others.",
    "Good progress! Keep up the momentum and aim for even better results.",
    "Work on improving your study habits and time management skills.",
    "Promoted on merit.",
    "Promoted on probation.",
    "Advise to withdraw."
  ];

  // Get predefined options from school settings or use defaults
  const predefinedComments = school?.settings?.remark?.comments || defaultComments;
  const predefinedRemarks = school?.settings?.remark?.remarks || defaultRemarks;

  // Prefill data when modal opens
  useEffect(() => {
    if (results) {
      setComment(results.comment || "");
      setRemark(results.remark || "");
      // Default to the computed position so the admin can just append context
      // to it (e.g. "1st" -> "1st in Upper Grade 1") instead of starting blank.
      setCustomPosition(results.custom_position || results.overall_rank || "");

      // Always map current school skills to saved ratings if available
      if (school?.settings?.remark?.skills) {
        const savedSkills = (results.skills || []).reduce((acc, skill) => {
          acc[skill.skill] = skill.rating;
          return acc;
        }, {});
        const formattedSkills = {};
        school.settings.remark.skills.forEach(skill => {
          formattedSkills[skill] = savedSkills[skill] || ""; // prefill or blank
        });
        setSkillsRatings(formattedSkills);
      } else {
        setSkillsRatings({});
      }
    }
  }, [results, school]);

  const handleRatingChange = (skill, rating) => {
    setSkillsRatings((prev) => ({ ...prev, [skill]: rating }));
  };

  const handleCommentTemplateChange = (e) => {
    const value = e.target.value;
    setSelectedCommentTemplate(value);
    if (value && value !== "custom") {
      setComment(value);
    } else if (value === "custom") {
      setComment("");
    }
  };

  const handleRemarkTemplateChange = (e) => {
    const value = e.target.value;
    setSelectedRemarkTemplate(value);
    if (value && value !== "custom") {
      setRemark(value);
    } else if (value === "custom") {
      setRemark("");
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!comment || !remark) {
      setError("Please fill in both the comment and remark.");
      setTimeout(() => setError(""), 5000);
      return;
    }

    const selectedSkills = Object.entries(skillsRatings).map(([skill, rating]) => ({
      skill,
      rating,
    }));

    if (selectedSkills.length !== school?.settings?.remark?.skills?.length) {
      setError("Please provide ratings for all skills.");
      setTimeout(() => setError(""), 5000);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        remark,
        comment,
        schoolId: school._id,
        // The term/session the result page was filtered to when the comment
        // was saved — not the school's live current term, which may differ
        // when reviewing a past term's result.
        session: session || school?.settings?.current_session,
        term: term || school?.settings?.current_term,
        skills: selectedSkills,
      };

      if (school?.settings?.use_custom_position) {
        payload.custom_position = customPosition;
      }

      await userRequest.post(`/result/remark/${childId}`, payload);

      setSuccess(true);
      toast.success("Comments saved successfully!");
      onCommentAdded();
      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      console.error(error);
      setError("An error occurred while saving the result.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <FiMessageSquare className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add General Review</h2>
              <p className="text-sm text-gray-600 mt-0.5">Enter student observation and comments</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center transition-colors"
          >
            <FiX className="w-5 h-5" />
            <span className="sr-only">Close modal</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Status Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <FiAlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                <span className="text-red-700 font-medium text-sm">{error}</span>
              </div>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center">
                <FiCheck className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                <span className="text-green-700 font-medium text-sm">Comments saved successfully!</span>
              </div>
            </div>
          )}

          <form onSubmit={submitHandler} className="space-y-6">
            {/* Custom Position */}
            {school?.settings?.use_custom_position && (
              <div>
                <label htmlFor="custom-position" className="block mb-2 text-sm font-semibold text-gray-900">
                  Position
                </label>
                <input
                  id="custom-position"
                  type="text"
                  value={customPosition}
                  onChange={(e) => setCustomPosition(e.target.value)}
                  placeholder='E.g. "First in Upper Grade 1"'
                  className="bg-gray-50 border-2 border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full p-3.5 transition-all"
                />
                <p className="mt-1.5 text-xs text-gray-500">Shown on the student's result PDF. The results page shows the automatically computed position for reference.</p>
              </div>
            )}

            {/* Teacher Comment */}
            <div>
              <label htmlFor="comment" className="block mb-2 text-sm font-semibold text-gray-900">
                Teacher Comment <span className="text-red-500">*</span>
              </label>
              <div className="mb-2">
                <select
                  id="comment-template"
                  value={selectedCommentTemplate}
                  onChange={handleCommentTemplateChange}
                  className="w-full bg-white border-2 border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-4 py-2.5 transition-all cursor-pointer"
                  disabled={loading}
                >
                  <option value="">Select a template or type custom...</option>
                  {predefinedComments.map((template, index) => (
                    <option key={index} value={template}>
                      {template.length > 60 ? `${template.substring(0, 60)}...` : template}
                    </option>
                  ))}
                  <option value="custom">Type custom comment...</option>
                </select>
              </div>
              <textarea
                id="comment"
                rows="3"
                onChange={(e) => {
                  setComment(e.target.value);
                  // Clear template selection if user manually edits
                  if (selectedCommentTemplate && e.target.value !== selectedCommentTemplate) {
                    setSelectedCommentTemplate("");
                  }
                }}
                value={comment}
                placeholder="E.g. Excellent performance throughout the term. Shows great improvement in all subjects."
                className="bg-gray-50 border-2 border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full p-3.5 transition-all resize-none"
                required
              />
              <p className="mt-1.5 text-xs text-gray-500">Select a template above or type your own custom comment</p>
            </div>

            {/* General Remark */}
            <div>
              <label htmlFor="remark" className="block mb-2 text-sm font-semibold text-gray-900">
                General Remark <span className="text-red-500">*</span>
              </label>
              <div className="mb-2">
                <select
                  id="remark-template"
                  value={selectedRemarkTemplate}
                  onChange={handleRemarkTemplateChange}
                  className="w-full bg-white border-2 border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-4 py-2.5 transition-all cursor-pointer"
                  disabled={loading}
                >
                  <option value="">Select a template or type custom...</option>
                  {predefinedRemarks.map((template, index) => (
                    <option key={index} value={template}>
                      {template.length > 60 ? `${template.substring(0, 60)}...` : template}
                    </option>
                  ))}
                  <option value="custom">Type custom remark...</option>
                </select>
              </div>
              <textarea
                id="remark"
                rows="3"
                onChange={(e) => {
                  setRemark(e.target.value);
                  // Clear template selection if user manually edits
                  if (selectedRemarkTemplate && e.target.value !== selectedRemarkTemplate) {
                    setSelectedRemarkTemplate("");
                  }
                }}
                value={remark}
                placeholder="E.g. Continue to maintain this excellent standard. Keep up the good work!"
                className="bg-gray-50 border-2 border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full p-3.5 transition-all resize-none"
                required
              />
              <p className="mt-1.5 text-xs text-gray-500">Select a template above or type your own custom remark</p>
            </div>

            {/* Skills Assessment */}
            {school?.settings?.remark?.skills && school.settings.remark.skills.length > 0 && (
              <div className="bg-gradient-to-br from-slate-50 to-indigo-50 border-2 border-indigo-100 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FiStar className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-base font-semibold text-gray-900">Skills Assessment</h3>
                </div>
                <div className="space-y-4">
                  {school.settings.remark.skills.map((skill, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-indigo-300 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <label className="text-sm font-semibold text-gray-700 flex-shrink-0">
                          {skill}
                        </label>
                        <select
                          onChange={(e) => handleRatingChange(skill, e.target.value)}
                          value={skillsRatings[skill] || ""}
                          className="flex-1 sm:max-w-xs bg-white border-2 border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 px-4 py-2.5 transition-all cursor-pointer"
                        >
                          <option value="">Select rating</option>
                          {school?.settings?.remark?.ratings?.map((rate, idx) => (
                            <option key={idx} value={rate}>
                              {rate}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-gray-600 text-center">
                  Please provide ratings for all skills listed above
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModal}
                disabled={loading}
                className="w-full sm:w-auto text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 font-semibold rounded-full text-sm px-6 py-2.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 font-semibold rounded-full text-sm px-6 py-2.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Save Comments
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCommentModal;
